import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppService } from '@app/app.service';
import { ArticleModel } from '@modules/article/models/article.model';
import { InformModalComponent } from '@modules/shared/modal/inform-modal/inform-modal.component';
import { Breadcrumb, BreadcrumbService } from '@modules/shared/services/breadcrumb.service';
import { DialogService } from '@modules/shared/services/dialog.service';
import { HeaderService } from '@modules/shared/services/header.service';
import { LocaleService } from '@modules/shared/services/locale.service';
import { ArticlePageService } from '@pages/article-page/article-page.service';
import { Locale } from 'src/environments/language.config';

@Injectable({
  providedIn: 'root',
})
export class ArticleResolver implements Resolve<Observable<ArticleModel | null>> {
  private appService = inject(AppService);
  private articlePageService = inject(ArticlePageService);
  private dialogService = inject(DialogService);
  private localeService = inject(LocaleService);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  public resolve(route: ActivatedRouteSnapshot): Observable<ArticleModel | null> {
    const slugOrId = route.paramMap.get('slugOrId');
    if (!slugOrId) {
      this.router.navigate(['404'], { skipLocationChange: true });
      return EMPTY;
    }

    return this.articlePageService.getArticle(slugOrId).pipe(
      map((result) => {
        if (result.error?.type === 'NOT_FOUND') {
          this.router.navigate(['404'], { skipLocationChange: true });
          return null;
        }

        if (result.error?.type === 'HTTP_ERROR') {
          this.router.navigate(['500'], { skipLocationChange: true });
          return null;
        }

        if (result.error?.type === 'NO_TRANSLATION') {
          const isEnglish = this.translateService.getCurrentLang() === Locale.EN;
          const targetLanguage = isEnglish ? Locale.DE : Locale.EN;
          const actionLabel =
            targetLanguage === Locale.DE
              ? 'ERRORS.API.NO_TRANSLATION.ACTION_DE'
              : 'ERRORS.API.NO_TRANSLATION.ACTION_EN';

          this.dialogService.openNext({
            component: InformModalComponent,
            disableCloseBtn: true,
            disableBackdropClose: true,
            inputs: {
              headline: 'ERRORS.API.NO_TRANSLATION.HEADLINE',
              message: 'ERRORS.API.NO_TRANSLATION.TEXT',
              actionLabel,
            },
            outputs: {
              requestAction: () => {
                this.dialogService.close();
                this.localeService.changeLanguage(targetLanguage);
              },
            },
          });

          return null;
        }

        const article = result.data;
        if (!article) return null;

        this.appService.darkBackgroundPage();
        this.appService.setTitle(`${article.title} | Mercedes-Benz Media`);
        return article;
      })
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class ArticleBreadcrumbResolver implements Resolve<Observable<Breadcrumb[] | Breadcrumb | undefined>> {
  private articlePageService = inject(ArticlePageService);
  private headerService = inject(HeaderService);
  private breadcrumbService = inject(BreadcrumbService);

  public resolve(route: ActivatedRouteSnapshot): Observable<Breadcrumb[] | Breadcrumb | undefined> {
    const slugOrId = route.paramMap.get('slugOrId');
    if (slugOrId) {
      if (this.breadcrumbService.keepBreadcrumbs) {
        return of({ label: 'PRESS_INFORMATION', link: `/article/${slugOrId}` });
      } else {
        // fetch article to build "default" breadcrumbs for this article
        return this.articlePageService.getArticle(slugOrId).pipe(
          switchMap((result) => {
            const article = result.data;
            if (article) {
              if (article.topicId) {
                return this.headerService.findNavigationMenuById(article.topicId).pipe(
                  switchMap((menu) => {
                    if (menu) {
                      return this.headerService.navigationMenuPath$(menu).pipe(
                        map((topicPath) => {
                          return [
                            ...topicPath.map((item) => ({
                              label: `MENU.${item.id}.NAME`,
                              link: `MENU.${item.id}.PATH`,
                            })),
                            { label: 'PRESS_INFORMATION', link: `/article/${article.id}` },
                          ];
                        })
                      );
                    } else {
                      return of([{ label: 'PRESS_INFORMATION', link: `/article/${article.id}` }]);
                    }
                  })
                );
              } else {
                return of([{ label: 'PRESS_INFORMATION', link: `/article/${article.id}` }]);
              }
            } else {
              return of(undefined);
            }
          })
        );
      }
    } else {
      return of(undefined);
    }
  }
}
